import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { EMPTY_DEPT, ServiceSection, FinanceSection, AccountingSection, ShareFrom, achProblems } from "@/components/SignDepartments";
import { api, useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, PenLine, Trash2, Download, FileText, Copy,
  Link as LinkIcon, UserPlus, Users, Send, RefreshCw, Clock, ExternalLink, Eye, Paperclip,
} from "lucide-react";
import { uploadDealerDocument } from "@/components/SignatureOnFileDialog";
import { usePdfPreview } from "@/components/PdfPreviewDialog";
import CommissionsSyncPanel from "@/components/CommissionsSyncPanel";
import LocationsPanel from "@/components/LocationsPanel";
import SharedSignaturePad from "@/components/SignaturePad";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ROLE_PRESETS = ["Dealer Principle", "General Manager", "Controller"];

// -------------------- Reusable canvas signature pad --------------------
function SignaturePad({ onChange, testId = "signature-pad" }) {
  // Delegates to the shared component so both dealer + agent flows stay in sync.
  return <SharedSignaturePad onChange={onChange} testId={testId} />;
}

// =============================================================================
// PUBLIC — /sign-dealer/:token
// =============================================================================
export function DealerSignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [sig, setSig] = useState(null);
  // { field_key: value } — the same value fills every occurrence of that key
  // across every form assigned to this recipient. Server pre-seeds defaults
  // (dealer-level values + this signer's bound location) so the signer only
  // types values that need to change.
  const [sharedFields, setSharedFields] = useState({});
  const [depts, setDepts] = useState({});
  const deptKeys = (data?.locations?.length ? data.locations.map((l) => l.id) : ["__dealer__"]);
  const deptFor = (k) => depts[k] || EMPTY_DEPT();
  const setDept = (k, v) => setDepts((p) => ({ ...p, [k]: v }));
  const copyDept = (toKey, fromKey, part) => { const src = deptFor(fromKey); setDept(toKey, { ...deptFor(toKey), [part]: JSON.parse(JSON.stringify(src[part])) }); toast.success("Copied from the other location"); };
  const [activeForm, setActiveForm] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // Mid-flight "+ Add location" widget state
  const [addingLoc, setAddingLoc] = useState(false);
  const [locDraft, setLocDraft] = useState(null);
  const [hasService, setHasService] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/dealers/sign/${token}`);
        setData(data);
        setSignerName(data.signer?.name || data.contact_name || "");
        // Prefill everything the server pre-resolved for us.
        if (data.defaults && Object.keys(data.defaults).length) {
          setSharedFields(data.defaults);
        }
        if (data.already_signed) setDone(true);
      } catch (e) {
        toast.error("Sign link invalid or expired");
      }
    })();
  }, [token]);

  const handleFieldChange = (fieldKey, value) => {
    setSharedFields((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const submitNewLocation = async () => {
    if (!locDraft?.name?.trim() && !locDraft?.physical_address?.trim()) {
      toast.error("Give the new location at least a name or address");
      return;
    }
    setAddingLoc(true);
    try {
      const fresh = await api.post(`/public/dealers/sign/${token}/locations`, locDraft);
      setData(fresh.data);
      setSharedFields(fresh.data.defaults || {});
      setLocDraft(null);
      toast.success(`Location "${locDraft.name || 'new store'}" added — this signer is now bound to it`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add location");
    } finally {
      setAddingLoc(false);
    }
  };

  const submit = async () => {
    if (!signerName.trim()) { toast.error("Type your name to continue"); return; }
    if (data.signature_needed && !sig) { toast.error("Draw your signature to continue"); return; }
    for (const k of Object.keys(depts)) {
      const a = depts[k].accounting;
      if (!a.delegate) { const probs = achProblems(a.ach); if (probs.length) { toast.error(`ACH: ${probs[0]}`); return; } }
      else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a.delegate_email || "")) { toast.error("Enter a valid email for the accounting contact"); return; }
    }
    setSubmitting(true);
    try {
      await api.post(`/public/dealers/sign/${token}`, {
        signer_name: signerName.trim(),
        signature_data_url: sig,
        values_by_key: sharedFields,
        departments: Object.fromEntries(Object.entries(depts).map(([k, v]) => [k, { ...v, accounting: { ...v.accounting, ach: v.accounting.delegate ? {} : v.accounting.ach, delegate_email: v.accounting.delegate ? v.accounting.delegate_email : "" } }])),
      });
      setDone(true);
      toast.success("Thank you — you're signed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  if (!data) return <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center text-sm text-[#6C757D]">Loading…</div>;
  const form = data.forms?.[activeForm];
  const groups = data.field_groups || [];
  const SECTIONS = [
    { key: "dealership", label: "1 · Dealership information", hint: "Legal entity, EIN, dealer principal and primary contact." },
    { key: "location", label: "Location", hint: data.location ? `This signer is bound to ${data.location.name || "this location"}. One store? Click “Use dealership info above”.` : "One store? Click “Use dealership info above”. More stores? Use “+ Add another location”." },
    { key: "service", label: "Service (template fields)" },
    { key: "banking", label: "Banking (template fields)" },
    { key: "signing", label: "Signer details" },
    { key: "other", label: "Other form fields" },
  ];
  const LOOKUP_FOR = (g) => (/dms/i.test(g.label) || g.field_key === "dms") ? "dms" : (/menu/i.test(g.label) || g.field_key === "menu_provider") ? "menu" : (/crm/i.test(g.label) || g.field_key === "crm") ? "crm" : null;
  const useDealershipAbove = () => {
    const pick = (keys) => keys.map((k) => sharedFields[k]).find(Boolean) || "";
    const byLabel = (re) => groups.find((g) => g.section === "dealership" && re.test(g.label))?.field_key;
    const next = { ...sharedFields };
    const map = [["location_name", ["dealership_name", byLabel(/dealership|business/i)]], ["location_address", ["physical_address", byLabel(/physical address/i)]], ["location_phone", ["phone", byLabel(/^phone/i)]], ["location_ein", ["ein", byLabel(/ein/i)]]];
    map.forEach(([to, from]) => { const v = pick(from.filter(Boolean)); if (v) next[to] = v; });
    groups.filter((g) => g.section === "location").forEach((g) => { if (!next[g.field_key]) { if (/address/i.test(g.label)) next[g.field_key] = pick(["physical_address"]); else if (/phone/i.test(g.label)) next[g.field_key] = pick(["phone"]); else if (/ein/i.test(g.label)) next[g.field_key] = pick(["ein"]); else if (/name/i.test(g.label) && !/manager|director/i.test(g.label)) next[g.field_key] = pick(["dealership_name"]); } });
    setSharedFields(next); toast.success("Location filled from dealership info");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="dealer-sign-page">
        <div className="mb-6 text-center">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Advantage Warranty Group</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#0B132B] mt-2 tracking-tight leading-none">Dealer packet</h1>
          <p className="text-sm text-[#6C757D] mt-2">
            {data.dealer_name} · {data.forms?.length || 0} form(s) assigned to you
            {data.signer?.label && <> · <span className="font-semibold text-[#0B132B]">{data.signer.label}</span></>}
          </p>
          {data.location && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-[#D4AF37]/50 bg-[#D4AF37]/10" data-testid="sign-location-pill">
              <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#D4AF37]">This location</span>
              <span className="text-sm font-semibold text-[#0B132B]">{data.location.name || "(unnamed store)"}</span>
              {data.location.physical_address && <span className="text-xs text-[#6C757D]">· {data.location.physical_address}</span>}
            </div>
          )}
          {!done && data.signer && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setLocDraft(locDraft ? null : { name: "", physical_address: "", phone: "", fi_managers: [""], general_manager: "", fi_director: "", office_manager: "", labor_rate: "", dms: "", menu_provider: "" })}
                className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#0B132B] hover:text-[#D4AF37]"
                data-testid="sign-add-location-btn"
              >
                {locDraft ? "× Cancel" : "+ Add another location for me"}
              </button>
            </div>
          )}
        </div>

        {locDraft && !done && (
          <Card className="p-6 border border-[#0B132B]/20 rounded-sm mb-4 bg-[#F8F9FA]" data-testid="sign-new-location-card">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B] mb-1">Add a new location</div>
            <div className="text-[11px] text-[#6C757D] mb-3">This signer will be rebound to the new store so the location fields auto-fill from it.</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={locDraft.name || ""} onChange={(e) => setLocDraft({ ...locDraft, name: e.target.value })} placeholder="Location name (e.g. West Side)" className="h-10 rounded-sm" data-testid="sign-new-loc-name" />
              <Input value={locDraft.physical_address || ""} onChange={(e) => setLocDraft({ ...locDraft, physical_address: e.target.value })} placeholder="Street, City, State ZIP" className="h-10 rounded-sm sm:col-span-1" data-testid="sign-new-loc-address" />
              <Input value={locDraft.phone || ""} onChange={(e) => setLocDraft({ ...locDraft, phone: e.target.value })} placeholder="Location phone" className="h-10 rounded-sm" data-testid="sign-new-loc-phone" />
              <Input value={locDraft.labor_rate || ""} onChange={(e) => setLocDraft({ ...locDraft, labor_rate: e.target.value })} placeholder="Labor rate ($/hr)" className="h-10 rounded-sm" data-testid="sign-new-loc-rate" />
              <Input value={locDraft.general_manager || ""} onChange={(e) => setLocDraft({ ...locDraft, general_manager: e.target.value })} placeholder="General manager" className="h-10 rounded-sm" data-testid="sign-new-loc-gm" />
              <Input value={(locDraft.fi_managers || [""])[0] || ""} onChange={(e) => setLocDraft({ ...locDraft, fi_managers: [e.target.value] })} placeholder="F&I manager" className="h-10 rounded-sm" data-testid="sign-new-loc-fi" />
              <Input value={locDraft.dms || ""} onChange={(e) => setLocDraft({ ...locDraft, dms: e.target.value })} placeholder="DMS (CDK, Reynolds, …)" list="lk-dms" className="h-10 rounded-sm" data-testid="sign-new-loc-dms" />
              <Input value={locDraft.menu_provider || ""} onChange={(e) => setLocDraft({ ...locDraft, menu_provider: e.target.value })} placeholder="Menu (MenuMetric, EasyCare, …)" list="lk-menu" className="h-10 rounded-sm" data-testid="sign-new-loc-menu" />
              <Input value={locDraft.crm || ""} onChange={(e) => setLocDraft({ ...locDraft, crm: e.target.value })} placeholder="CRM (VinSolutions, Elead, …)" list="lk-crm" className="h-10 rounded-sm" data-testid="sign-new-loc-crm" />
              <Input value={locDraft.fi_director || ""} onChange={(e) => setLocDraft({ ...locDraft, fi_director: e.target.value })} placeholder="Finance director" className="h-10 rounded-sm" data-testid="sign-new-loc-fidir" />
              <Input value={locDraft.service_manager || ""} onChange={(e) => setLocDraft({ ...locDraft, service_manager: e.target.value })} placeholder="Service manager" className="h-10 rounded-sm" data-testid="sign-new-loc-svc" />
              <Input value={locDraft.ein || ""} onChange={(e) => setLocDraft({ ...locDraft, ein: e.target.value })} placeholder="Location EIN (if different)" className="h-10 rounded-sm" data-testid="sign-new-loc-ein" />
            </div>
            <div className="mt-3 flex justify-end">
              <Button onClick={submitNewLocation} disabled={addingLoc} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="sign-new-loc-submit">
                {addingLoc ? "Adding…" : "Save location"}
              </Button>
            </div>
          </Card>
        )}

        {done ? (
          <Card className="p-10 border-green-200 bg-green-50 rounded-sm text-center" data-testid="sign-thanks">
            <CheckCircle size={40} className="mx-auto text-green-600" />
            <div className="mt-3 text-xl font-bold text-[#0B132B]">Thank you — you're signed</div>
            <p className="text-sm text-[#6C757D] mt-2">
              We've notified our team. Your agreement is now executed and your Advantage representative will be in touch to set up your programs.
            </p>
          </Card>
        ) : (
          <>
            {/* Form review tabs (if >1 form) */}
            {data.forms?.length > 1 && (
              <div className="flex gap-1 overflow-x-auto border-b border-[#E9ECEF] mb-4" data-testid="sign-form-tabs">
                {data.forms.map((f, i) => (
                  <button key={f.id} type="button" onClick={() => setActiveForm(i)}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.14em] font-bold whitespace-nowrap border-b-2 transition-colors ${
                      activeForm === i ? "border-[#D4AF37] text-[#0B132B]" : "border-transparent text-[#6C757D] hover:text-[#0B132B]"
                    }`}
                    data-testid={`sign-tab-${i}`}>
                    {i + 1}. {f.name}
                  </button>
                ))}
              </div>
            )}

            {/* PDF review card */}
            <Card className="p-6 border-[#E9ECEF] rounded-sm space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">Review — {form?.name}</div>
                  <a href={`${BACKEND}/api/public/dealers/sign/${token}/forms/${form?.id}/agreement.pdf`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-bold text-[#0B132B] hover:text-[#D4AF37]"
                    data-testid="download-pdf-link">
                    <Download size={12}/> Download PDF
                  </a>
                </div>
                <div className="max-h-[520px] overflow-y-auto border border-[#E9ECEF] rounded-sm bg-[#F8F9FA] p-3 space-y-3">
                  {Array.from({ length: form?.page_count || 0 }, (_, i) => (
                    <img key={i}
                      src={`${BACKEND}/api/public/dealers/sign/${token}/forms/${form.id}/page/${i + 1}.png`}
                      alt={`Page ${i + 1}`}
                      className="w-full block shadow-sm bg-white"
                      loading={i === 0 ? "eager" : "lazy"}
                      data-testid={`sign-page-image-${i + 1}`}
                    />
                  ))}
                </div>
              </div>
              {data.forms?.length > 1 && (
                <div className="flex justify-between text-xs">
                  <Button type="button" size="sm" variant="outline" disabled={activeForm === 0}
                    onClick={() => setActiveForm((i) => Math.max(0, i - 1))} className="rounded-sm" data-testid="sign-prev-form">
                    ← Previous form
                  </Button>
                  <div className="text-[#6C757D] self-center">Form {activeForm + 1} of {data.forms.length}</div>
                  <Button type="button" size="sm" variant="outline" disabled={activeForm >= data.forms.length - 1}
                    onClick={() => setActiveForm((i) => Math.min(data.forms.length - 1, i + 1))} className="rounded-sm" data-testid="sign-next-form">
                    Next form →
                  </Button>
                </div>
              )}
            </Card>

            {/* Deduped fields — one input per shared key, fanned out server-side, grouped by section. */}
            {groups.length > 0 && (
              <Card className="p-6 border-[#E9ECEF] rounded-sm mt-4 space-y-5" data-testid="dedupe-fields-card">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">Your details</div>
                  <div className="text-[11px] text-[#6C757D] mt-1">
                    Each value below fills every form in this packet that needs it — you only type it once.
                  </div>
                </div>
                {SECTIONS.filter((s) => groups.some((g) => g.section === s.key && g.kind !== "signature")).map((s) => (
                  <div key={s.key} className="border border-[#E9ECEF] rounded-sm p-4" data-testid={`sign-section-${s.key}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#D4AF37]">{s.label}</div>{s.hint && <div className="text-[11px] text-[#6C757D]">{s.hint}</div>}</div>
                      {s.key === "location" && (
                        <button type="button" onClick={useDealershipAbove} className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#0B132B] underline underline-offset-2 hover:text-[#D4AF37]" data-testid="sign-use-address-above">Use dealership info above</button>
                      )}
                    </div>
                    {s.key === "service" && (
                      <label className="flex items-center gap-2 text-xs text-[#0B132B] mb-3" data-testid="sign-service-toggle">
                        <input type="checkbox" checked={hasService} disabled={data.dealer_type === "franchise"} onChange={(e) => setHasService(e.target.checked)} className="accent-[#0B132B]" />
                        This location has a service department{data.dealer_type === "franchise" ? " (franchise — always yes)" : ""}
                      </label>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {groups.filter((g) => g.section === s.key && g.kind !== "signature" && !(s.key === "service" && /labor/i.test(g.label) && !hasService)).map((g) => {
                        const list = LOOKUP_FOR(g);
                        return (
                          <div key={g.field_key}>
                            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">{g.label}</Label>
                            <Input
                              type={g.kind === "date" ? "date" : "text"}
                              value={sharedFields[g.field_key] || ""}
                              onChange={(e) => handleFieldChange(g.field_key, e.target.value)}
                              placeholder={list ? "Choose or type…" : g.label}
                              list={list ? `lk-${list}` : undefined}
                              className="mt-1 h-10 rounded-sm"
                              data-testid={`dedupe-field-${g.field_key}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {Object.entries(data.lookups || {}).map(([k, opts]) => <datalist key={k} id={`lk-${k}`}>{(opts || []).map((o) => <option key={o} value={o} />)}</datalist>)}
              </Card>
            )}

            {/* Structured departments — per location */}
            <Card className="p-6 border-[#E9ECEF] rounded-sm mt-4 space-y-5" data-testid="departments-card">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">Departments</div>
                <div className="text-[11px] text-[#6C757D] mt-1">Who we work with at {deptKeys.length > 1 ? "each location" : "the dealership"}. If something is the same for several locations, use “Same as another location…”.</div>
              </div>
              {deptKeys.map((k, li) => {
                const loc = (data.locations || []).find((l) => l.id === k);
                const dep = deptFor(k);
                const set = (v) => setDept(k, v);
                return (
                  <div key={k} className="space-y-4" data-testid={`dept-loc-${k}`}>
                    {deptKeys.length > 1 && <div className="text-sm font-bold text-[#0B132B] border-b border-[#E9ECEF] pb-1">Location {li + 1} · {loc?.name || loc?.physical_address || "Store"}</div>}
                    {[["service", "2 · Service department", "Service manager, advisors and staff."], ["finance", "3 · Finance department", "Finance directors / managers, plus the CRM, DMS and Menu systems in use (three answers, even if they're the same)."], ["accounting", "4 · Accounting & ACH", "Accounting contacts and banking. Routing and account numbers must be entered twice and match."]].map(([part, title, hint]) => (
                      <div key={part} className="border border-[#E9ECEF] rounded-sm p-4" data-testid={`sign-${part}-${k}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <div><div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#D4AF37]">{title}</div><div className="text-[11px] text-[#6C757D]">{hint}</div></div>
                          {deptKeys.length > 1 && <ShareFrom locations={(data.locations || [])} currentKey={k} onCopy={(from) => copyDept(k, from, part)} />}
                        </div>
                        {part === "service" && <ServiceSection dep={dep} set={set} />}
                        {part === "finance" && <FinanceSection dep={dep} set={set} />}
                        {part === "accounting" && <AccountingSection dep={dep} set={set} />}
                      </div>))}
                  </div>
                );
              })}
            </Card>

            {/* Signature — applied to every dealer signature slot across your forms */}
            <Card className="p-6 border-[#0B132B]/20 rounded-sm mt-4 space-y-4" data-testid="global-sign-card">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Your name (as it should appear)</Label>
                <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="e.g. Jane Smith"
                  className="mt-1 h-11 rounded-sm" data-testid="signer-name-input" />
              </div>
              {data.signature_needed && (
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-2 flex items-center gap-1.5">
                    <PenLine size={12} /> Signature — used to sign every signature slot on your assigned form(s)
                  </Label>
                  <SignaturePad onChange={setSig} testId="dealer-signature-pad" />
                </div>
              )}
              <Button onClick={submit} disabled={submitting || !signerName.trim() || (data.signature_needed && !sig)}
                className="w-full h-12 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold text-sm uppercase tracking-[0.14em]"
                data-testid="dealer-sign-submit">
                {submitting ? "Submitting…" : `Sign & submit ${data.forms?.length || 1} form(s)`}
              </Button>
              {(!signerName.trim() || (data.signature_needed && !sig)) && (
                <p className="text-[11px] text-[#6C757D] text-center -mt-2">
                  {!signerName.trim() ? "Type your name to continue. " : ""}
                  {data.signature_needed && !sig ? "Draw your signature above to continue." : ""}
                </p>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

// =============================================================================
// AUTHENTICATED — /dealers/:id (admin/agency-admin/agent detail + Signers Builder)
// =============================================================================
export default function DealerDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [sig, setSig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [forms, setForms] = useState([]);
  const [sendMode, setSendMode] = useState("parallel");
  const [markOpen, setMarkOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const [markForm, setMarkForm] = useState({ signed_at: new Date().toISOString().slice(0, 10), signer_name: "", file: null });
  const nav = navigate;
  const markSigned = async () => {
    setMarking(true);
    try {
      const { data } = await api.post(`/dealers/${id}/mark-signed`, { signed_at: markForm.signed_at || undefined, signer_name: markForm.signer_name || undefined });
      if (markForm.file) { try { await uploadDealerDocument(id, markForm.file, `Signed agreement (paper) — ${markForm.file.name}`); } catch { toast.warning("Marked signed, but the file upload failed — add it from the dealer profile"); } }
      toast.success("Marked signed — dealer is in the pipeline");
      nav(data.onboarding_id ? `/onboarding/${data.onboarding_id}` : `/dealers/${id}/profile`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark as signed"); }
    finally { setMarking(false); }
  };
  const [sending, setSending] = useState(false);

  // New-signer form state
  const [newSigner, setNewSigner] = useState({
    name: "",
    email: "",
    labelPreset: ROLE_PRESETS[0],   // dropdown selection
    labelCustom: "",                // used when preset === "custom"
    form_ids: new Set(),
    location_id: "",                // Phase 2 — bind signer to a location
  });
  const [addingSigner, setAddingSigner] = useState(false);
  const [deletingDealer, setDeletingDealer] = useState(false);

  const deleteDealer = async () => {
    if (!window.confirm(`Delete dealer "${d?.dealer_name}"? This removes the dealer record, signers, packet history, and any executed agreements. This cannot be undone. Use Archive if you might need it later.`)) return;
    setDeletingDealer(true);
    try {
      await api.delete(`/dealers/${id}`);
      toast.success(`Deleted ${d.dealer_name}`);
      navigate("/dealers");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
      setDeletingDealer(false);
    }
  };
  // Provider filter for the packet-form checkbox list in the add-signer form.
  const [signerFormProvider, setSignerFormProvider] = useState("all");

  const load = useCallback(async () => {
    try {
      const [dRes, fRes] = await Promise.all([
        api.get(`/dealers/${id}`),
        api.get("/dealer-forms").catch(() => ({ data: [] })),
      ]);
      setD(dRes.data);
      setForms(fRes?.data || []);
      setSendMode(dRes.data?.send_mode || "parallel");
    } catch (e) {
      console.error("[DealerDetail] load failed:", e);
      toast.error("Failed to load dealer");
    }
    // `api`, `toast`, and the setters are module-stable — only `id` invalidates this callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const packetForms = useMemo(() => {
    if (!d?.form_ids) return [];
    return d.form_ids.map((fid) => forms.find((f) => f.id === fid)).filter(Boolean);
  }, [d, forms]);

  const toggleSignerForm = (fid) => {
    setNewSigner((prev) => {
      const next = new Set(prev.form_ids);
      if (next.has(fid)) next.delete(fid); else next.add(fid);
      return { ...prev, form_ids: next };
    });
  };

  const addSigner = async () => {
    const label = newSigner.labelPreset === "custom"
      ? newSigner.labelCustom.trim()
      : newSigner.labelPreset;
    if (!newSigner.email.trim()) { toast.error("Signer email is required"); return; }
    if (!label) { toast.error("Signer role is required"); return; }
    if (newSigner.form_ids.size === 0) { toast.error("Assign at least one form to this signer"); return; }
    setAddingSigner(true);
    try {
      await api.post(`/dealers/${id}/signers`, {
        email: newSigner.email.trim(),
        name: newSigner.name.trim() || null,
        label,
        form_ids: Array.from(newSigner.form_ids),
        location_id: newSigner.location_id || null,
      });
      toast.success("Signer added");
      setNewSigner({ name: "", email: "", labelPreset: ROLE_PRESETS[0], labelCustom: "", form_ids: new Set(), location_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add signer");
    } finally { setAddingSigner(false); }
  };

  const removeSigner = async (s) => {
    if (!window.confirm(`Remove signer ${s.email}?`)) return;
    try {
      await api.delete(`/dealers/${id}/signers/${s.id}`);
      toast.success("Signer removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendPacket = async () => {
    if (!(d.signers || []).length) { toast.error("Add at least one signer before sending"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/dealers/${id}/send-packet`, { mode: sendMode });
      toast.success(`Packet sent (${sendMode}) to ${data.sent_to.length} signer(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const resendSigner = async (s) => {
    try {
      await api.post(`/dealers/${id}/signers/${s.id}/resend`);
      toast.success(`Resent to ${s.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copyLink = async (url) => {
    if (!url) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else {
        const ta = document.createElement("textarea");
        ta.value = url; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Sign link copied");
    } catch { toast.error("Couldn't copy — please copy manually"); }
  };

  const { preview, dialog: pdfDialog } = usePdfPreview();
  const [regenerating, setRegenerating] = useState(false);
  const regenerateExecuted = async () => {
    setRegenerating(true);
    try {
      await api.post(`/dealers/${id}/executed/regenerate`);
      toast.success("Executed copy refreshed with current dealer info");
      downloadExecuted();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to refresh"); }
    finally { setRegenerating(false); }
  };
  const downloadExecuted = () => {
    preview({
      fetcher: () => api.get(`/dealers/${id}/executed.pdf`, { responseType: "blob" }),
      filename: `${d?.dealer_name || "dealer"}-executed.pdf`,
      title: `Executed Agreement · ${d?.dealer_name || ""}`,
    });
  };

  if (!d) return <div className="min-h-screen bg-[#F8F9FA]"><Navbar /><main className="max-w-3xl mx-auto px-6 py-10 text-sm text-[#6C757D]">Loading…</main></div>;

  const isAdmin = user?.role === "admin";
  const canEditSigners = ["admin", "agency_admin", "agent"].includes(user?.role) && d.status !== "executed";
  const signers = d.signers || [];
  const canMarkSigned = canEditSigners && ["draft", "sent"].includes(d.status);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="dealer-detail-page">
        <Link to="/dealers" className="inline-flex items-center gap-2 text-sm text-[#6C757D] hover:text-[#0B132B] mb-4">
          <ArrowLeft size={14}/> Back to dealers
        </Link>
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">{(d.status || "").replace("_", " ")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#0B132B] mt-1">{d.dealer_name}</h1>
          <p className="text-sm text-[#6C757D] mt-1">{d.contact_name} · {d.contact_email}</p>
        </div>

        {canMarkSigned && (
          <Card className="p-4 border-emerald-300 bg-emerald-50/40 rounded-sm mb-6" data-testid="mark-signed-card">
            {!markOpen ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><div className="text-sm font-bold text-[#0B132B]">Already signed on paper or outside the portal?</div>
                  <div className="text-xs text-[#6C757D]">Skip e-signature — mark it signed and push the dealer straight into the onboarding pipeline.</div></div>
                <Button onClick={() => setMarkOpen(true)} variant="outline" className="h-9 rounded-sm border-emerald-600 text-emerald-800 hover:bg-emerald-100" data-testid="mark-signed-btn"><CheckCircle size={13} className="mr-1.5" /> Mark as already signed</Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-4 gap-3 items-end">
                <div><Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Date signed</Label>
                  <Input type="date" value={markForm.signed_at} onChange={(e) => setMarkForm({ ...markForm, signed_at: e.target.value })} className="h-9 rounded-sm mt-1" data-testid="mark-signed-date" /></div>
                <div><Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Signed by</Label>
                  <Input value={markForm.signer_name} onChange={(e) => setMarkForm({ ...markForm, signer_name: e.target.value })} placeholder={d.dealer_principal || d.contact_name || "Name"} className="h-9 rounded-sm mt-1" data-testid="mark-signed-name" /></div>
                <div><Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Scanned agreement (optional)</Label>
                  <label className="mt-1 flex items-center gap-2 h-9 px-3 border border-dashed border-[#0B132B]/30 rounded-sm text-xs text-[#0B132B] cursor-pointer bg-white truncate">
                    <Paperclip size={13} className="shrink-0" /><span className="truncate">{markForm.file ? markForm.file.name : "Attach PDF / image"}</span>
                    <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => setMarkForm({ ...markForm, file: e.target.files?.[0] || null })} data-testid="mark-signed-file" />
                  </label></div>
                <div className="flex gap-2">
                  <Button onClick={markSigned} disabled={marking} className="h-9 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="mark-signed-confirm">{marking ? "Saving…" : "Confirm & push to pipeline"}</Button>
                  <Button variant="outline" onClick={() => setMarkOpen(false)} className="h-9 rounded-sm">Cancel</Button>
                </div>
              </div>)}
          </Card>)}

        <Card className="p-5 border-[#E9ECEF] rounded-sm mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Row label="Created" val={d.created_at} isDate />
            <Row label="Sent" val={d.sent_at} isDate />
            <Row label="Dealer signed" val={d.signed_at} isDate />
            <Row label="Executed" val={d.countersigned_at} isDate />
            <Row label="Dealer signer" val={d.dealer_signer_name} />
            <Row label="AWG signer" val={d.admin_signer_name} />
          </div>
          {isAdmin && (
            <div className="mt-4 pt-4 border-t border-[#E9ECEF] flex justify-end">
              <Button
                onClick={deleteDealer}
                disabled={deletingDealer}
                variant="outline"
                size="sm"
                className="h-9 rounded-sm border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                data-testid="dealer-delete-btn"
                title="Hard-delete this dealer and everything on it (use to clear demo/training content)"
              >
                <Trash2 size={13} className="mr-1.5" /> {deletingDealer ? "Deleting…" : "Delete dealer"}
              </Button>
            </div>
          )}
        </Card>

        {/* Commissions platform sync — visible only to super-admins, after countersign */}
        {isAdmin && d.countersigned_at && (
          <div className="mb-6">
            <CommissionsSyncPanel
              kind="dealer"
              id={d.id}
              status={d.commissions_sync_status}
              syncedAt={d.commissions_synced_at}
              syncError={d.commissions_sync_error}
              syncActor={d.commissions_sync_actor}
              syncTrigger={d.commissions_sync_trigger}
              onChange={load}
            />
          </div>
        )}

        {/* Packet forms */}
        {packetForms.length > 0 && (
          <Card className="p-5 border-[#E9ECEF] rounded-sm mb-6" data-testid="packet-forms-card">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B] mb-3">Packet ({packetForms.length} form(s))</div>
            <div className="space-y-2">
              {packetForms.map((f) => {
                const signed = (d.signed_form_ids || []).includes(f.id);
                return (
                  <div key={f.id} className="flex items-center gap-3 p-2.5 bg-[#F8F9FA] border border-[#E9ECEF] rounded-sm text-sm" data-testid={`packet-form-row-${f.id}`}>
                    <FileText size={14} className="text-[#0B132B] shrink-0"/>
                    <div className="flex-1 min-w-0 truncate">
                      <span className="text-[#0B132B] font-semibold">{f.name}</span>
                      {f.provider && f.provider !== "Advantage Warranty Group" && (
                        <span className="ml-2 text-[9px] uppercase tracking-[0.14em] font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-1.5 py-0.5 rounded-sm">{f.provider}</span>
                      )}
                    </div>
                    <Link
                      to={`/dealer-forms/${f.id}`}
                      className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D] hover:text-[#0B132B]"
                      data-testid={`packet-form-map-${f.id}`}
                      title="Open field mapper"
                    >
                      Map fields →
                    </Link>
                    <span className={`text-[9px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 border rounded-sm ${signed ? "text-green-700 bg-green-50 border-green-200" : "text-[#6C757D] bg-white border-[#E9ECEF]"}`}>
                      {signed ? "Signed" : "Pending"}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* ============ SIGNERS BUILDER ============ */}
        {canEditSigners && (
          <LocationsPanel
            dealerId={id}
            locations={d.locations || []}
            achGroups={d.ach_groups || []}
            onChange={load}
          />
        )}

        {canEditSigners && (
          <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="signers-builder-card">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Multi-signer routing</div>
                <div className="text-lg font-bold text-[#0B132B] flex items-center gap-2"><Users size={16}/> Signers</div>
                <p className="text-[11px] text-[#6C757D] mt-1">Add every person who needs to sign — assign each of them the specific forms they're responsible for. Each signer gets their own scoped link.</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Delivery</div>
                <select value={sendMode} onChange={(e) => setSendMode(e.target.value)}
                  className="h-9 border border-[#0B132B]/20 rounded-sm bg-white px-2 text-xs font-semibold text-[#0B132B]"
                  data-testid="signer-send-mode">
                  <option value="parallel">Parallel — email everyone at once</option>
                  <option value="sequential">Sequential — one signer at a time</option>
                </select>
                <Button onClick={sendPacket} disabled={sending || signers.length === 0}
                  className="h-9 rounded-sm bg-[#D4AF37] hover:bg-[#B8942F] text-[#0B132B] font-bold"
                  data-testid="send-packet-btn">
                  <Send size={13} className="mr-1.5"/> {sending ? "Sending…" : "Send Packet"}
                </Button>
              </div>
            </div>

            {/* Existing signers */}
            {signers.length > 0 ? (
              <div className="space-y-2 mb-6">
                {signers.map((s) => (
                  <div key={s.id} className="p-3 border border-[#E9ECEF] rounded-sm bg-white flex items-center gap-3 flex-wrap"
                    data-testid={`signer-row-${s.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-[#0B132B]">{s.name || s.email}</span>
                        <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-2 py-0.5 rounded-sm">{s.label}</span>
                        {s.location_id && (() => {
                          const loc = (d.locations || []).find((l) => l.id === s.location_id);
                          return loc ? (
                            <span className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#0B132B] bg-[#F8F9FA] border border-[#E9ECEF] px-2 py-0.5 rounded-sm" data-testid={`signer-location-${s.id}`}>
                              @ {loc.name || loc.physical_address || "Location"}
                            </span>
                          ) : null;
                        })()}
                        <span className={`text-[9px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 border rounded-sm ${
                          s.status === "completed" ? "text-green-700 bg-green-50 border-green-200" :
                          s.status === "sent" ? "text-blue-700 bg-blue-50 border-blue-200" :
                          "text-[#6C757D] bg-white border-[#E9ECEF]"
                        }`} data-testid={`signer-status-${s.id}`}>{s.status}</span>
                      </div>
                      <div className="text-[11px] text-[#6C757D] truncate">{s.email} · {s.form_ids?.length || 0} form(s)</div>
                    </div>
                    {s.sign_url && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => copyLink(s.sign_url)}
                          className="h-8 rounded-sm border-[#0B132B]/20 text-[#0B132B]"
                          data-testid={`signer-copy-${s.id}`} title="Copy sign link">
                          <Copy size={12}/>
                        </Button>
                        <a href={s.sign_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center h-8 px-2 border border-[#0B132B]/20 rounded-sm text-[#0B132B] hover:bg-[#F8F9FA]"
                          data-testid={`signer-open-${s.id}`} title="Open sign link">
                          <ExternalLink size={12}/>
                        </a>
                      </>
                    )}
                    {s.status !== "completed" && (
                      <Button size="sm" variant="outline" onClick={() => resendSigner(s)}
                        className="h-8 rounded-sm border-[#0B132B]/20 text-[#0B132B]"
                        data-testid={`signer-resend-${s.id}`} title="Resend invite email">
                        <RefreshCw size={12} className="mr-1"/> Resend
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => removeSigner(s)}
                      className="h-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`signer-delete-${s.id}`} title="Remove signer">
                      <Trash2 size={12}/>
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 border-2 border-dashed border-[#E9ECEF] rounded-sm text-center text-sm text-[#6C757D] mb-6">
                No signers yet. Add the first signer below.
              </div>
            )}

            {/* Add-signer form */}
            <div className="p-4 border border-[#0B132B]/20 rounded-sm bg-[#F8F9FA]" data-testid="add-signer-form">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-3 flex items-center gap-1.5">
                <UserPlus size={12}/> Add signer
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Name</Label>
                  <Input value={newSigner.name}
                    onChange={(e) => setNewSigner({ ...newSigner, name: e.target.value })}
                    placeholder="Jane Smith" className="mt-1 h-10 rounded-sm" data-testid="new-signer-name" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Email *</Label>
                  <Input type="email" value={newSigner.email}
                    onChange={(e) => setNewSigner({ ...newSigner, email: e.target.value })}
                    placeholder="jane@dealership.com" className="mt-1 h-10 rounded-sm" data-testid="new-signer-email" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Role / Title *</Label>
                  <select value={newSigner.labelPreset}
                    onChange={(e) => setNewSigner({ ...newSigner, labelPreset: e.target.value })}
                    className="mt-1 h-10 w-full border border-[#0B132B]/20 rounded-sm bg-white px-2 text-sm text-[#0B132B]"
                    data-testid="new-signer-role-preset">
                    {ROLE_PRESETS.map((r) => <option key={r} value={r}>{r}</option>)}
                    <option value="custom">Other (type a custom title)</option>
                  </select>
                </div>
                {newSigner.labelPreset === "custom" && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Custom title *</Label>
                    <Input value={newSigner.labelCustom}
                      onChange={(e) => setNewSigner({ ...newSigner, labelCustom: e.target.value })}
                      placeholder="e.g. Bookkeeper" className="mt-1 h-10 rounded-sm"
                      data-testid="new-signer-role-custom" />
                  </div>
                )}
                {(d.locations || []).length > 0 && (
                  <div className={newSigner.labelPreset === "custom" ? "sm:col-span-2" : ""}>
                    <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Location (optional)</Label>
                    <select value={newSigner.location_id}
                      onChange={(e) => setNewSigner({ ...newSigner, location_id: e.target.value })}
                      className="mt-1 h-10 w-full border border-[#0B132B]/20 rounded-sm bg-white px-2 text-sm text-[#0B132B]"
                      data-testid="new-signer-location">
                      <option value="">— No location binding (dealer-level signer)</option>
                      {(d.locations || []).map((l) => (
                        <option key={l.id} value={l.id}>{l.name || l.physical_address || `Location ${l.id.slice(0, 6)}`}</option>
                      ))}
                    </select>
                    <div className="text-[10px] text-[#6C757D] mt-1">
                      Binds F&I manager, GM, labor rate, DMS, Menu → this location's values on their packet.
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Assign forms *</Label>
                {packetForms.length === 0 ? (
                  <div className="text-[11px] text-[#6C757D] italic mt-1">
                    No forms in this packet yet. Send an initial packet first from the Dealers list to select the forms.
                  </div>
                ) : (() => {
                  const providers = Array.from(new Set(packetForms.map((f) => f.provider || "Advantage Warranty Group"))).sort();
                  const visible = signerFormProvider === "all" ? packetForms : packetForms.filter((f) => (f.provider || "Advantage Warranty Group") === signerFormProvider);
                  return (
                    <>
                      {providers.length > 1 && (
                        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="signer-form-provider-filter">
                          {["all", ...providers].map((p) => {
                            const on = signerFormProvider === p;
                            const count = p === "all" ? packetForms.length : packetForms.filter((x) => (x.provider || "Advantage Warranty Group") === p).length;
                            return (
                              <button
                                key={p} type="button"
                                onClick={() => setSignerFormProvider(p)}
                                className={`text-[10px] uppercase tracking-[0.14em] font-bold px-2.5 py-1 rounded-sm border transition-colors ${on ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#0B132B]" : "border-[#E9ECEF] bg-white text-[#6C757D] hover:border-[#0B132B]/30"}`}
                                data-testid={`signer-form-provider-chip-${p}`}
                              >
                                {p === "all" ? "All" : p} · {count}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {visible.map((f) => {
                          const checked = newSigner.form_ids.has(f.id);
                          return (
                            <label key={f.id} className={`flex items-start gap-2 p-2 border rounded-sm text-xs cursor-pointer transition-colors ${checked ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#E9ECEF] bg-white hover:bg-[#F1F3F5]"}`}
                              data-testid={`new-signer-form-${f.id}`}>
                              <input type="checkbox" checked={checked} onChange={() => toggleSignerForm(f.id)} className="mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="truncate font-semibold text-[#0B132B]">{f.name}</div>
                                {f.provider && f.provider !== "Advantage Warranty Group" && (
                                  <div className="text-[9px] uppercase tracking-[0.12em] font-bold text-[#D4AF37]">{f.provider}</div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={addSigner} disabled={addingSigner || packetForms.length === 0}
                  className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-bold"
                  data-testid="add-signer-btn">
                  <UserPlus size={14} className="mr-1.5"/> {addingSigner ? "Adding…" : "Add signer"}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Legacy fallback: full-packet sign link (no per-signer routing) */}
        {d.sign_url && d.status !== "executed" && signers.length === 0 && (
          <Card className="p-4 border-[#E9ECEF] rounded-sm mb-6 flex items-center gap-3 flex-wrap" data-testid="sign-link-card">
            <LinkIcon size={16} className="text-[#0B132B] shrink-0"/>
            <div className="flex-1 min-w-0 truncate">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Dealer sign link (whole packet)</div>
              <div className="text-xs font-mono text-[#0B132B] truncate">{d.sign_url}</div>
            </div>
            <Button size="sm" variant="outline" className="rounded-sm h-9"
              onClick={() => copyLink(d.sign_url)}
              data-testid="copy-sign-link-btn">
              <Copy size={12} className="mr-1.5"/> Copy
            </Button>
          </Card>
        )}

        {isAdmin && d.status === "dealer_signed" && (
          <Card className="p-6 border-amber-200 bg-amber-50 rounded-sm" data-testid="auto-execute-pending">
            <div className="text-sm font-bold text-[#0B132B]">Dealer signed — finalizing</div>
            <p className="text-xs text-[#6C757D] mt-1">Dealer agreements execute automatically on signature (no countersign). If this record stays here, click Refresh below to rebuild the executed copy.</p>
            <Button onClick={regenerateExecuted} disabled={regenerating} variant="outline" className="mt-3 h-9 rounded-sm border-[#0B132B]/20" data-testid="auto-execute-refresh">{regenerating ? "Refreshing…" : "Refresh"}</Button>
          </Card>
        )}

        {d.status === "executed" && (
          <Card className="p-6 border-green-200 bg-green-50 rounded-sm text-center">
            <CheckCircle size={32} className="mx-auto text-green-600" />
            <div className="mt-2 font-bold text-[#0B132B]">Fully executed</div>
            <p className="text-sm text-[#6C757D] mt-1">The dealer has signed; the agreement executed automatically and the dealer is in the pipeline.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Button onClick={downloadExecuted} className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="download-executed-btn">
                <Eye size={14} className="mr-2"/> Preview & Download executed copy
              </Button>
              <Button onClick={regenerateExecuted} disabled={regenerating} variant="outline" className="h-10 rounded-sm border-[#0B132B]/20" data-testid="regenerate-executed-btn">
                {regenerating ? "Refreshing…" : "Refresh with current dealer info"}
              </Button>
            </div>
          </Card>
        )}
        {pdfDialog}
      </main>
    </div>
  );
}

function Row({ label, val, isDate = false }) {
  const display = !val ? "—" : (isDate ? new Date(val).toLocaleString() : val);
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">{label}</div>
      <div className="text-sm font-semibold text-[#0B132B] mt-0.5">{display}</div>
    </div>
  );
}
